import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FileInput } from '../../components/ui/FileInput';
import { useCompanyStore } from '../../store/company';
import type { Company } from '../../types';

const companySchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  address: z.string().min(1, 'Address is required'),
  vatNumber: z.string().min(1, 'VAT number is required'),
  logo: z.string().nullable(),
});

type CompanyFormData = z.infer<typeof companySchema>;

export function SettingsPage() {
  const { company, updateCompany } = useCompanyStore();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: company || undefined,
  });

  const onSubmit = async (data: CompanyFormData) => {
    try {
      await updateCompany(data);
      alert('Company settings updated successfully');
    } catch (error) {
      console.error('Failed to update company settings:', error);
      alert('Failed to update company settings');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your company information and preferences
        </p>
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <FileInput
                value={company?.logo}
                onChange={(base64) => setValue('logo', base64)}
                error={errors.logo?.message}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <Input {...register('name')} error={errors.name?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  VAT Number
                </label>
                <Input
                  {...register('vatNumber')}
                  error={errors.vatNumber?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <Input
                  type="email"
                  {...register('email')}
                  error={errors.email?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <Input {...register('phone')} error={errors.phone?.message} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Address
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              {errors.address?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.address.message}
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}